import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { CoachState, useWalterCoach } from "@/providers/walter-coach";
import { AssistantDesktopDownloadLinks, CoachSmartCatalogPayload, CoachSuggestionPayload, walterAssistantClient } from "@/services/walter-assistant";
import { UserType } from "@/types/user";
import { ArrowUpLeft, Download, RefreshCw, ShoppingCart } from "lucide-react";
import { buildThemeDetailUrl } from "./funder-table/funder-table.utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { CoachSuggestionsDrawer } from "./coach-suggestions-drawer";
import { FunderTableDrawer } from "./funder-table/funder-table-drawer";
import { LiveCoachSignal, LiveCoachSignalEvent } from "./live-coach-signal";
import { PhaseRoadmapSection } from "./phase-roadmap-timeline";
import { resolveMockFromHash } from "./smart-catalog/__mocks__/sample-payload";
import { SmartCatalogDrawer } from "./smart-catalog/smart-catalog-drawer";

function readStoredDrawerOpen(key: string, defaultValue: boolean): boolean {
    try {
        const raw = localStorage.getItem(key);
        if (raw !== null) return raw === "true";
    } catch { /* ignore */ }
    return defaultValue;
}

type Props = {
    isOpen: boolean;
    user: UserType | null;
};

function resolveSuggestionAccentKey(suggestion: CoachSuggestionPayload): string | undefined {
    if (suggestion.detected_signal?.code) return suggestion.detected_signal.code;
    if (suggestion.source === "layer_2_conductor") return "layer_2_conductor";
    if (suggestion.source === "manual") return "manual";
    return suggestion.suggestion_code ?? undefined;
}

type LiveCoachSignalStatus = "not_connected" | "idle" | "on_call" | "completed";

function coachStateToSignalStatus(state: CoachState): LiveCoachSignalStatus {
    switch (state) {
        case "open_in_call":
            return "on_call";
        case "open_post_call":
            return "completed";
        case "open_idle":
            return "idle";
        case "not_connected":
        default:
            return "not_connected";
    }
}

export function WalterIaCoachPanel({ isOpen, user }: Props) {
    const hasCoachAccess = Boolean(user?.walter_assistant_coach);
    const {
        suggestions,
        serverTime,
        isLoading,
        errorText,
        sessionReplaced,
        resumeSession,
        reloadInitialSuggestions,
        coachState,
        callState,
        smartCatalog,
        isSmartCatalogPending,
        isVerbatimPending,
        refreshSmartCatalog,
        smartCatalogOptions,
        overrideSmartCatalog,
        requestVerbatims,
        fundingBasket,
        isFundingBasketPending,
        removeBasketItem,
        setEnvelopeOverride,
        clearSuggestions
    } = useWalterCoach();
    const [downloadLinks, setDownloadLinks] = useState<AssistantDesktopDownloadLinks>({});
    const [isSuggestionsDrawerOpen, setIsSuggestionsDrawerOpen] = useState(() => readStoredDrawerOpen("coach-suggestions-drawer-open", false));
    const [isSmartCatalogOpen, setIsSmartCatalogOpen] = useState(false);
    const [isFunderTableOpen, setIsFunderTableOpen] = useState(() => readStoredDrawerOpen("coach-funder-table-drawer-open", false));

    /*
     * Injection mock dev pour itérer visuellement sans backend. Activable
     * via `#smart-catalog-mock` (ou `=success`, `=degraded`, `=empty`).
     * Gated derrière `import.meta.env.DEV` — strippé du bundle prod par
     * le tree-shaking de Vite (DEV est une constante remplacée à la
     * compilation).
     */
    const mockSmartCatalog = useMemo<CoachSmartCatalogPayload | null>(() => {
        if (!import.meta.env.DEV) return null;
        return resolveMockFromHash(window.location.hash);
    }, []);
    const effectiveSmartCatalog = mockSmartCatalog ?? smartCatalog;

    const handleSetSuggestionsDrawerOpen = useCallback((open: boolean) => {
        setIsSuggestionsDrawerOpen(open);
        try { localStorage.setItem("coach-suggestions-drawer-open", String(open)); } catch { /* ignore */ }
    }, []);

    const handleSetFunderTableOpen = useCallback((open: boolean) => {
        setIsFunderTableOpen(open);
        try { localStorage.setItem("coach-funder-table-drawer-open", String(open)); } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        if (!isOpen || !hasCoachAccess) return;
        let cancelled = false;
        walterAssistantClient
            .getDesktopDownloadLinks()
            .then(links => {
                if (!cancelled) setDownloadLinks(links);
            })
            .catch(error => {
                console.error("Walter Assistant desktop manifest error:", error);
            });
        return () => {
            cancelled = true;
        };
    }, [hasCoachAccess, isOpen]);

    useEffect(() => {
        if (!isOpen || !hasCoachAccess) return;
        void reloadInitialSuggestions();
    }, [hasCoachAccess, isOpen, reloadInitialSuggestions]);

    const orderedCoachSuggestions = useMemo(() => {
        return [...suggestions].sort((a, b) => b.timestamp_ms - a.timestamp_ms);
    }, [suggestions]);

    /*
     * URL vers la fiche catalogue du premier thème éligible dans le panier.
     * Affiché comme lien rapide dans le drawer "Conseils".
     */
    const topThemeUrl = useMemo<string | null>(() => {
        if (!fundingBasket?.funders?.length) return null;
        const professionCode = fundingBasket.profession_code ?? "";
        for (const funder of fundingBasket.funders) {
            if (funder.source === "not_eligible") continue;
            for (const theme of funder.themes ?? []) {
                const url = buildThemeDetailUrl(theme, professionCode, funder.funding_code);
                if (url) return url;
            }
        }
        return null;
    }, [fundingBasket]);

    const liveSignalStatus: LiveCoachSignalStatus = coachStateToSignalStatus(coachState);
    const liveSignalLastEvent: LiveCoachSignalEvent | undefined = useMemo(() => {
        const top = orderedCoachSuggestions[0];
        if (!top) return undefined;
        /*
         * Préférence : on tape la clé d'accent sur `detected_signal.code` (le
         * signal sémantique côté backend : `price_objection`, etc.) plutôt
         * que sur `suggestion_code` — c'est aligné avec ce que voit l'agent
         * dans la chip de la carte juste en dessous.
         */
        const accentKey = resolveSuggestionAccentKey(top) ?? "coach.suggestion";
        return {
            id: top.suggestion_id,
            type: accentKey,
            timestamp: top.timestamp_ms
        };
    }, [orderedCoachSuggestions]);

    const hasDownloadLinks = Boolean(downloadLinks.mac || downloadLinks.windows);
    const isNotConnected = coachState === "not_connected";
    /*
     * Le tableau financeur (jauges + thèmes phares par financeur) n'a de
     * sens que pour les playbooks santé — les playbooks généralistes n'ont
     * pas de notion de profession/mode d'exercice pilotant les enveloppes.
     * `catalog_origins` vient de `Playbook.catalog_origins` (poussé dans
     * `coach.call_state`) ; absent/inconnu ⇒ on masque par défaut.
     */
    const isSantePlaybook = Boolean(callState?.catalog_origins?.includes("sante"));

    if (!hasCoachAccess) {
        return <div className="flex h-full items-center justify-center text-sm text-gray-500">Le coach live n&apos;est pas activé pour cet utilisateur.</div>;
    }

    const isInCall = coachState === "open_in_call";
    const isPostCall = coachState === "open_post_call";
    /*
     * En appel OU en recap post-appel, on déploie le panneau (roadmap +
     * objections + suggestions). En idle / not_connected, on centre
     * verticalement le `LiveCoachSignal` (deux spacers en flex-grow).
     */
    const isExpanded = isInCall || isPostCall;

    return (
        <div className="relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-white ">
            {sessionReplaced ? (
                <div className="mb-2 flex items-center justify-between gap-2 rounded-lg bg-red-50 px-4 py-2 text-[11px] text-red-800">
                    <span>Le coach a été ouvert ailleurs (autre onglet ou client desktop)</span>
                    <Button variant="destructive_outline" size="sm" onClick={resumeSession}>
                        <RefreshCw className="mr-1.5 inline-block size-3" aria-hidden />
                        Reprendre ici
                    </Button>
                </div>
            ) : null}

            <div aria-hidden className={cn("min-h-0 basis-0 transition-[flex-grow] duration-500 ease-out", isExpanded ? "grow-0" : "grow")} />

            {isExpanded ? (
                <div className="flex border-b">
                    {isInCall ? (
                        <button
                            type="button"
                            onClick={() => handleSetSuggestionsDrawerOpen(true)}
                            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 border-r p-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-primary"
                        >
                            <ArrowUpLeft className="size-4 shrink-0" aria-hidden />
                            Conseils
                        </button>
                    ) : null}
                    {isSantePlaybook ? (
                        <button
                            type="button"
                            onClick={() => handleSetFunderTableOpen(true)}
                            className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 border-r p-2 text-sm text-gray-500 hover:bg-gray-50 hover:text-primary"
                        >
                            <ShoppingCart className="size-4 shrink-0" aria-hidden />
                            Tableau financeur
                        </button>
                    ) : null}
                    {/* Smart catalog button hidden — le tableau financeur est la vue principale */}
                </div>
            ) : null}

            <LiveCoachSignal status={liveSignalStatus} lastEvent={liveSignalLastEvent} className="shrink-0" />

            {isNotConnected && hasDownloadLinks ? (
                <div className="mx-4 mt-4 shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                    <p className="text-sm text-gray-600">Téléchargez la dernière version de l&apos;application desktop pour activer le coach live.</p>
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                        {downloadLinks.mac ? (
                            <a
                                href={downloadLinks.mac}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-200 hover:text-blue-700"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Download className="size-4 shrink-0" aria-hidden />
                                Télécharger pour Mac
                            </a>
                        ) : null}
                        {downloadLinks.windows ? (
                            <a
                                href={downloadLinks.windows}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-200 hover:text-blue-700"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Download className="size-4 shrink-0" aria-hidden />
                                Télécharger pour Windows
                            </a>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <div
                className={cn(
                    "flex min-h-0 basis-0 flex-col overflow-hidden p-2 transition-[flex-grow,opacity,margin-top] duration-500 ease-out",
                    isExpanded ? "grow opacity-100" : "grow-0 opacity-0"
                )}
                aria-hidden={!isExpanded}
            >
                <ScrollArea className="min-h-0 flex-1">
                    <div className="rounded-lg bg-gray-50 p-4">
                        <PhaseRoadmapSection callState={callState} isLoading={isLoading} isInCall={isInCall} onPlaybookSwitched={clearSuggestions} />
                    </div>
                </ScrollArea>
            </div>

            <div aria-hidden className={cn("min-h-0 basis-0 transition-[flex-grow] duration-500 ease-out", isExpanded ? "grow-0" : "grow")} />

            <CoachSuggestionsDrawer
                isOpen={isOpen && isSuggestionsDrawerOpen && isInCall}
                onClose={() => handleSetSuggestionsDrawerOpen(false)}
                suggestions={orderedCoachSuggestions}
                serverTime={serverTime}
                isLoading={isLoading}
                errorText={errorText}
                coachState={coachState}
                playbookName={callState?.playbook_name}
                topThemeUrl={topThemeUrl}
            />

            <FunderTableDrawer
                isOpen={isOpen && isFunderTableOpen && isExpanded && isSantePlaybook}
                onClose={() => handleSetFunderTableOpen(false)}
                payload={fundingBasket}
                isPending={isFundingBasketPending}
                options={smartCatalogOptions}
                onOverrideProfile={payload => void overrideSmartCatalog(payload)}
                onRemoveItem={input => void removeBasketItem(input)}
                onSetEnvelopeOverride={input => void setEnvelopeOverride(input)}
            />

            <SmartCatalogDrawer
                isOpen={isOpen && isSmartCatalogOpen && isExpanded}
                onClose={() => setIsSmartCatalogOpen(false)}
                payload={effectiveSmartCatalog}
                isPending={isSmartCatalogPending && !mockSmartCatalog}
                isVerbatimPending={isVerbatimPending && !mockSmartCatalog}
                onRefresh={() => void refreshSmartCatalog()}
                coachState={coachState}
                playbookName={callState?.playbook_name}
                options={mockSmartCatalog ? null : smartCatalogOptions}
                onOverride={mockSmartCatalog ? undefined : payload => void overrideSmartCatalog(payload)}
                onRevealThemes={mockSmartCatalog ? undefined : themeCodes => void requestVerbatims(themeCodes)}
            />
        </div>
    );
}
